import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { STATUS_SITE_URL } from "../../lib/env";

const cols = [
    {
        title: "Product",
        links: [
            { label: "Pricing", to: "/pricing" },
            { label: "Compare", to: "/compare" },
            { label: "Startups (Free)", to: "/startups" },
            { label: "Use Cases", to: "/use-cases" },
            { label: "System Status", href: STATUS_SITE_URL, external: true },
        ],
    },
    {
        title: "Company",
        links: [
            { label: "About", to: "/about" },
            { label: "Roadmap", to: "/roadmap" },
            { label: "Careers", to: "/careers" },
            { label: "Case Studies", to: "/case-studies" },
            { label: "Contact sales", to: "/contact-sales" },
            
            
        ],
    },
    {
        title: "Resources",
        links: [
            { label: "Blog", to: "/blog" },
            // { label: "Docs", to: "/docs" },
            { label: "Help & Community", to: "/help" },
            { label: "FAQs", to: "/faqs" },
            { label: "Changelog", to: "/changelog" },
        ],
    },
    {
        title: "Legal",
        links: [
            { label: "Policies", to: "/policies" },
            { label: "Security", to: "/security" },
            // { label: "Compliance", to: "/compliance" },

        ],
    },
];

export const CTAFooter = () => {
    return (
        <footer
            className="relative pt-24 pb-12 overflow-hidden border-t border-white/[0.06]"
            data-testid="cta-footer"
        >
            <div className="absolute inset-0 radial-glow opacity-70 pointer-events-none" />
            <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="rounded-3xl border border-white/10 bg-gradient-to-br from-obsidian-100 via-obsidian-200 to-obsidian-300 p-10 sm:p-16 text-center relative overflow-hidden"
                    data-testid="cta-block"
                >
                    <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[80%] h-40 bg-electric/30 blur-[120px] rounded-full pointer-events-none" />
                    <p className="relative text-xs uppercase tracking-[0.3em] text-amber-glow font-semibold font-code">
                        // the next chapter of your company
                    </p>
                    <h2 className="relative font-outfit text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mt-5 leading-[1.02] max-w-3xl mx-auto">
                        Scale through{" "}
                        <span className="gradient-text">systems</span>,
                        <br />
                        not through chaos.
                    </h2>
                    <p className="relative mt-5 text-zinc-400 max-w-xl mx-auto font-light">
                        Replace the stack. Unify the work. See the company clearly.
                    </p>
                    <div className="relative mt-9 flex flex-wrap items-center justify-center gap-3">
                        <Link
                            to="/pricing"
                            className="group inline-flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-b from-electric to-electric-deep btn-glow px-6 py-3.5 rounded-full hover:brightness-110 transition"
                            data-testid="footer-cta-primary"
                        >
                            Purchase Now
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                        </Link>
                        <button
                            onClick={() => window.open("https://demo.apexbrain.in", "_blank", "noopener")}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-white glass px-6 py-3.5 rounded-full hover:bg-white/10 transition"
                            data-testid="footer-cta-secondary"
                        >
                            Visit Demo
                            <ArrowUpRight className="w-4 h-4 text-electric" />
                        </button>
                    </div>
                </motion.div>

                {/* Footer columns */}
                <div className="mt-20 grid grid-cols-2 md:grid-cols-6 gap-8">
                    <div className="col-span-2 md:col-span-2">
                        <Link
                            to="/"
                            className="flex items-center gap-2.5"
                            data-testid="footer-logo"
                        >
                            <img
                                src="/favicon.png"
                                alt="ApexBrain"
                                className="w-8 h-8 rounded-lg shadow-[0_0_18px_rgba(94,234,212,0.35)]"
                            />
                            <span className="font-vireon text-xl tracking-wide text-white">
                                Apex<span className="bg-gradient-to-r from-cyan-300 to-emerald-400 bg-clip-text text-transparent">Brain</span>
                            </span>
                        </Link>
                        <p className="mt-4 text-sm text-zinc-500 leading-relaxed max-w-xs font-light">
                            The unified intelligence layer for modern teams.
                            Run seven core functions of your company from one
                            workspace.
                        </p>
                        <a
                            href={STATUS_SITE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-6 inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-white transition"
                        >
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse-glow" />
                            All systems operational
                        </a>
                    </div>

                    {cols.map((c) => (
                        <div key={c.title}>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-semibold">
                                {c.title}
                            </div>
                            <ul className="mt-4 space-y-2.5">
                                {c.links.map((l) => (
                                    <li key={l.label}>
                                        {l.href ? (
                                            <a
                                                href={l.href}
                                                target={l.external ? "_blank" : undefined}
                                                rel={
                                                    l.external
                                                        ? "noopener noreferrer"
                                                        : undefined
                                                }
                                                className="text-sm text-zinc-400 hover:text-white transition"
                                            >
                                                {l.label}
                                            </a>
                                        ) : (
                                            <Link
                                                to={l.to}
                                                className="text-sm text-zinc-400 hover:text-white transition"
                                            >
                                                {l.label}
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="mt-16 pt-6 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-xs text-zinc-600">
                        © {new Date().getFullYear()} ApexBrain · Unified
                        Intelligence Layer
                    </p>
                    <div className="flex items-center gap-5 text-xs text-zinc-500">
                        <Link
                            to="/policies?policy=privacy"
                            className="hover:text-white transition"
                        >
                            Privacy
                        </Link>
                        <Link
                            to="/policies?policy=terms"
                            className="hover:text-white transition"
                        >
                            Terms
                        </Link>
                        <Link to="/security" className="hover:text-white transition">
                            Security
                        </Link>
                        <Link to="/faqs" className="hover:text-white transition">
                            FAQs
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};
